'use client';

import { Button, Portal, Skeleton } from '@common/components';
import {
  FOCUS_RING,
  INTERACTIVE_SURFACE,
} from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useInView } from 'react-intersection-observer';

import { AuthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { PointCoin } from '@/app/_components/PointCoin';
import {
  CURRENCY_TAB,
  PENDING_REWARD_SECTION_ID,
} from '@/app/(private)/points/constants';
import { QuerySuspenseErrorBoundary } from '@/components/organism/QuerySuspenseErrorBoundary';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';
import { ROUTES, SELECTOR } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { useLoginHref } from '../_hooks/useLoginHref';

const SECTION_CLASS_NAME =
  'flex flex-col gap-8 bg-Gray-white px-20 pb-24 pt-20 xl:bg-transparent xl:p-0';

const MEMBER_SECTION_CARD_CLASS_NAME =
  'gap-12 pt-32 xl:gap-20 xl:rounded-medium xl:border xl:border-Gray-100 xl:bg-Gray-white xl:p-24';

const BALANCE_CARD_CLASS_NAME =
  'flex min-w-0 flex-1 flex-col gap-8 rounded-medium p-12';

const CHEVRON_CLASS_NAME =
  'size-16 shrink-0 text-Gray-700 transition group-hover:translate-x-1 group-hover:text-Gray-900 group-focus-visible:translate-x-1 group-focus-visible:text-Gray-900';

const PENDING_CHEVRON_CLASS_NAME =
  'size-16 shrink-0 text-Gray-700 transition group-hover:translate-x-1 group-focus-visible:translate-x-1';

const WELLO_POINT_HISTORY_HREF = ROUTES.POINTS.withSearchParams({
  searchParams: { currency: CURRENCY_TAB.CASH },
});

const PARTNER_POINT_HISTORY_HREF = ROUTES.POINTS.withSearchParams({
  searchParams: { currency: CURRENCY_TAB.POINT },
});

const AmountFallback = () => (
  <Skeleton typo="h5" className="w-64 rounded-xxsmall" />
);

interface PointAmountProps {
  className: string;
}

const WelloPointAmount = ({ className }: PointAmountProps) => {
  const { data: totalPoints } = useSuspenseQuery({
    ...AuthQueryOptions['get:/wello/v2/auth/point'](),
    select: (data) => data?.context?.totalPoints ?? 0,
  });

  return <span className={className}>{totalPoints.toLocaleString()}</span>;
};

const PartnerPointAmount = ({ className }: PointAmountProps) => {
  const { data: balance } = useSuspenseQuery({
    ...AuthQueryOptions[
      'get:/wello/v1/auth/affiliate/partner-points/summary'
    ](),
    select: ({ context }) => Math.max(0, context?.balance ?? 0),
  });

  return <span className={className}>{balance.toLocaleString()}</span>;
};

const PendingRewardRow = () => {
  const { data: pendingRewardAmount } = useSuspenseQuery({
    ...AuthQueryOptions['get:/wello/v1/auth/affiliate/wello-points/summary'](),
    select: ({ context }) => context?.pendingRewardAmount ?? 0,
  });

  return (
    <CustomLink
      href={`${WELLO_POINT_HISTORY_HREF}#${PENDING_REWARD_SECTION_ID}`}
      className={cn(
        'group flex items-center justify-between gap-8 rounded-medium border border-Gray-200 bg-Gray-25/50 px-16 py-12',
        INTERACTIVE_SURFACE,
      )}
    >
      <span className="font-700 text-Gray-900 typo-body4">
        웰포인트 적립예정
      </span>
      <span className="flex items-center gap-12">
        <span className="font-700 text-Gray-800 typo-body1">
          {pendingRewardAmount.toLocaleString()}원
        </span>
        <SvgChevronright className={PENDING_CHEVRON_CLASS_NAME} />
      </span>
    </CustomLink>
  );
};

const MemberPointSummary = () => (
  <QuerySuspenseErrorBoundary
    errorFallback={({ resetErrorBoundary }) => (
      <div className="flex min-h-120 flex-col items-center justify-center gap-12 rounded-small bg-Gray-50">
        <p className="font-500 text-Gray-600 typo-body4">불러오지 못했어요</p>
        <Button theme="sub-line" dimensions="H40" onClick={resetErrorBoundary}>
          다시 시도
        </Button>
      </div>
    )}
  >
    <div className="flex gap-12">
      <CustomLink
        href={WELLO_POINT_HISTORY_HREF}
        className={cn(
          'group bg-Y-25 transition-colors duration-150 hover:bg-Y-50 active:bg-Y-50 motion-reduce:transition-none',
          BALANCE_CARD_CLASS_NAME,
          FOCUS_RING,
        )}
      >
        <span className="flex items-center gap-4 font-600 text-Gray-800 typo-body4">
          웰포인트
          <SvgChevronright className={cn(CHEVRON_CLASS_NAME, 'size-12')} />
        </span>
        <span className="flex items-center gap-4">
          <PointCoin currency="WELLO_POINT" size={25} />
          <Suspense fallback={<AmountFallback />}>
            <WelloPointAmount className="font-700 text-Y-600 typo-h5" />
          </Suspense>
          <span className="font-700 text-Y-600 typo-body2">원</span>
        </span>
      </CustomLink>
      <CustomLink
        href={PARTNER_POINT_HISTORY_HREF}
        className={cn(
          'group bg-B-25 transition-colors duration-150 hover:bg-B-50 active:bg-B-50 motion-reduce:transition-none',
          BALANCE_CARD_CLASS_NAME,
          FOCUS_RING,
        )}
      >
        <span className="flex items-center gap-4 font-600 text-Gray-800 typo-body4">
          제휴포인트
          <SvgChevronright className={cn(CHEVRON_CLASS_NAME, 'size-12')} />
        </span>
        <span className="flex items-center gap-4">
          <PointCoin currency="AFFILIATE_PARTNER_POINT" size={28} />
          <Suspense fallback={<AmountFallback />}>
            <PartnerPointAmount className="font-700 text-B-550 typo-h5" />
          </Suspense>
          <span className="font-700 text-B-550 typo-body2">P</span>
        </span>
      </CustomLink>
    </div>
    <ErrorBoundary fallback={null}>
      <Suspense fallback={null}>
        <PendingRewardRow />
      </Suspense>
    </ErrorBoundary>
  </QuerySuspenseErrorBoundary>
);

const GuestPointSummary = () => {
  const loginHref = useLoginHref();

  return (
    <div className="flex flex-col gap-16 bg-Gray-white pt-12 xl:rounded-large xl:border xl:border-Gray-100 xl:px-20 xl:py-28">
      <div className="flex flex-col gap-8">
        <h2 className="font-700 text-Gray-900 typo-body2">
          나의 포인트를 확인하고 <br className="xl:hidden" />
          다양한 혜택에 참여해보세요!
        </h2>
        <p className="font-600 text-Gray-600 typo-caption1">
          웰로 회원에게 제공되는 다양한 포인트 적립 혜택을 확인해보세요.
        </p>
      </div>
      <Button asChild dimensions="H48" className="w-full">
        <CustomLink href={loginHref}>로그인하기</CustomLink>
      </Button>
    </div>
  );
};

const STICKY_AMOUNT_CLASS_NAME = 'flex items-center gap-4 font-700 typo-body2';

const StickyAmountFallback = () => (
  <Skeleton typo="body2" className="w-40 rounded-xxsmall" />
);

const MemberStickyPoints = () => (
  <ErrorBoundary fallback={null}>
    <span className="flex items-center gap-12 xl:gap-16">
      <span className={cn(STICKY_AMOUNT_CLASS_NAME, 'text-Y-600')}>
        <PointCoin currency="WELLO_POINT" size={17} />
        <span>
          <Suspense fallback={<StickyAmountFallback />}>
            <WelloPointAmount className="tabular-nums" />
          </Suspense>
          원
        </span>
      </span>
      <span className={cn(STICKY_AMOUNT_CLASS_NAME, 'text-B-550')}>
        <PointCoin currency="AFFILIATE_PARTNER_POINT" size={17} />
        <span>
          <Suspense fallback={<StickyAmountFallback />}>
            <PartnerPointAmount className="tabular-nums" />
          </Suspense>
          P
        </span>
      </span>
    </span>
  </ErrorBoundary>
);

const GuestStickyLogin = () => {
  const loginHref = useLoginHref();

  return (
    <CustomLink
      href={loginHref}
      className={cn(
        'rounded-xxsmall font-700 text-Gray-800 underline typo-body3 hover:text-Gray-900 active:text-Gray-900 xl:typo-body2',
        FOCUS_RING,
      )}
    >
      로그인하기
    </CustomLink>
  );
};

interface PointStickyBarProps {
  isGuest: boolean;
  isVisible: boolean;
}

//* 헤더 sticky 묶음 안에 걸어야 오버스크롤 바운스 때 헤더와 함께 움직인다(트렌드 키워드 바와 같은 방식).
const PointStickyBar = ({ isGuest, isVisible }: PointStickyBarProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);

  return (
    <Portal
      renderTo={() =>
        document.getElementById(
          isDesktop
            ? SELECTOR.DESKTOP_HEADER_ATTACHMENT
            : SELECTOR.MOBILE_HEADER_ATTACHMENT,
        )
      }
      deps={[isDesktop]}
    >
      <div
        aria-hidden={!isVisible}
        inert={!isVisible}
        data-header-attached={isVisible || undefined}
        className={cn(
          'border-b border-Gray-300 bg-Gray-white shadow-[0px_3px_5px_rgba(0,0,0,0.12)] transition-opacity duration-200 motion-reduce:transition-none xl:shadow-none',
          !isVisible && 'pointer-events-none opacity-0',
        )}
      >
        <DesktopPageShell
          variant="rail"
          rightAside={<div className="hidden w-300 shrink-0 xl:block" />}
        >
          <div className="flex h-56 items-center justify-between gap-12 px-20 xl:h-auto xl:px-0 xl:py-20">
            <span className="font-600 text-Gray-800 typo-body3 xl:font-700 xl:typo-body2">
              내 포인트
            </span>
            {isGuest ? <GuestStickyLogin /> : <MemberStickyPoints />}
          </div>
        </DesktopPageShell>
      </div>
    </Portal>
  );
};

interface BenefitPointSummaryProps {
  isGuest: boolean;
}

export const BenefitPointSummary = ({
  isGuest: serverIsGuest,
}: BenefitPointSummaryProps) => {
  const isGuest = useIsGuest(serverIsGuest);
  //* 고정 헤더(PC 72)에 가려진 부분은 화면 밖으로 본다.
  const { ref, inView } = useInView({
    initialInView: true,
    rootMargin: '-72px 0px 0px 0px',
  });

  return (
    <>
      <section
        ref={ref}
        className={cn(
          SECTION_CLASS_NAME,
          !isGuest && MEMBER_SECTION_CARD_CLASS_NAME,
        )}
      >
        {isGuest ? <GuestPointSummary /> : <MemberPointSummary />}
      </section>
      <PointStickyBar isGuest={isGuest} isVisible={!inView} />
    </>
  );
};
